import { ChevronLeft } from 'lucide-react';
import type { ElementType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './Icon';
import styles from './Layout.module.css';

export const layout = styles;

export function Page({
  children,
  width = 'wide',
  label,
  as: Element = 'section',
}: {
  children: ReactNode;
  width?: 'wide' | 'medium' | 'narrow' | 'slim';
  label?: string;
  as?: ElementType;
}) {
  const extra =
    width === 'narrow'
      ? styles.narrow
      : width === 'medium'
        ? `${styles.narrow} ${styles.medium}`
        : width === 'slim'
          ? `${styles.narrow} ${styles.slim}`
          : '';
  return (
    <Element className={`${styles.page} ${extra}`} aria-label={label}>
      {children}
    </Element>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={styles.back}>
      <Icon icon={ChevronLeft} size={18} />
      {children}
    </Link>
  );
}

export function NumberedSteps({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className={styles.steps}>
      {steps.map((step, index) => (
        <li key={index} className={styles.stepItem}>
          <span className={styles.stepNumber} aria-hidden="true">
            {index + 1}
          </span>
          <span className={styles.stepText}>{step}</span>
        </li>
      ))}
    </ol>
  );
}

const BOOKING_STEPS = ['Your details', 'Payment', 'Confirmed'];

export function BookingProgress({ current }: { current: 0 | 1 | 2 }) {
  return (
    <ol className={styles.progress} aria-label="Booking steps">
      {BOOKING_STEPS.map((label, index) => (
        <li
          key={label}
          aria-current={index === current ? 'step' : undefined}
          className={[
            styles.progressItem,
            index < current ? styles.progressDone : '',
            index === current ? styles.progressCurrent : '',
          ].join(' ')}
        >
          <span className={styles.progressBar} />
          <span>
            {index + 1}. {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function DescriptionList({ rows }: { rows: Array<[ReactNode, ReactNode] | null | false> }) {
  return (
    <dl className={styles.dl}>
      {rows.filter(Boolean).map((row, index) => {
        const [term, value] = row as [ReactNode, ReactNode];
        return (
          <div key={index} style={{ display: 'contents' }}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        );
      })}
    </dl>
  );
}
